import { useCallback, useEffect, useId, useRef, useState } from 'react'

import { useStepValidation } from './StepValidationContext'

type UseRegisterSaveGuardOptions = {
  /** Typed or AI-filled values differ from what is saved. */
  hasUnsavedEdits: boolean
  /**
   * Values Save would write differ from what is saved, including system values
   * such as defaults, computed fields, and branch writes.
   */
  hasUnsavedEditsOrSystemValues?: boolean
  save: () => Promise<void>
  /** Checked synchronously after save acknowledgement before navigation. */
  canProceed?: () => boolean
}

type UseRegisterSaveGuardReturn = {
  open: boolean
  isSaving: boolean
  onSaveAndContinue: () => Promise<void>
  onDiscardAndContinue: () => void
  onCancel: () => void
}

/**
 * Registers unsaved edits with step validation and returns navigation modal state.
 * AI pre-fill uses the same edit registration to avoid reading persisted stale inputs.
 */
export const useRegisterSaveGuard = ({
  hasUnsavedEdits,
  hasUnsavedEditsOrSystemValues,
  save,
  canProceed = () => true,
}: UseRegisterSaveGuardOptions): UseRegisterSaveGuardReturn => {
  const promptOnLeave = hasUnsavedEditsOrSystemValues ?? hasUnsavedEdits
  const { setNavigationGuard, setHasUnsavedEdits } = useStepValidation()
  const sourceId = useId()
  const [open, setOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const proceedRef = useRef<(() => void) | null>(null)
  const navigationRequestRef = useRef(0)
  const canProceedRef = useRef(canProceed)
  canProceedRef.current = canProceed

  useEffect(() => {
    if (!promptOnLeave) {
      setNavigationGuard(null)
      return
    }

    setNavigationGuard((proceed) => {
      proceedRef.current = proceed
      navigationRequestRef.current += 1
      setOpen(true)
    })

    return () => {
      setNavigationGuard(null)
    }
  }, [promptOnLeave, setNavigationGuard])

  useEffect(() => {
    setHasUnsavedEdits(sourceId, hasUnsavedEdits)
    return () => {
      setHasUnsavedEdits(sourceId, false)
    }
  }, [hasUnsavedEdits, setHasUnsavedEdits, sourceId])

  const onSaveAndContinue = useCallback(async () => {
    const requestedProceed = proceedRef.current
    if (!requestedProceed) return
    const requestedNavigation = navigationRequestRef.current
    setIsSaving(true)
    try {
      await save()
      if (
        proceedRef.current === requestedProceed &&
        navigationRequestRef.current === requestedNavigation &&
        canProceedRef.current()
      ) {
        proceedRef.current = null
        navigationRequestRef.current += 1
        setOpen(false)
        requestedProceed()
      }
    } finally {
      setIsSaving(false)
    }
  }, [save])

  const onDiscardAndContinue = useCallback(() => {
    const requestedProceed = proceedRef.current
    proceedRef.current = null
    navigationRequestRef.current += 1
    setOpen(false)
    requestedProceed?.()
  }, [])

  const onCancel = useCallback(() => {
    proceedRef.current = null
    navigationRequestRef.current += 1
    setOpen(false)
  }, [])

  return {
    open,
    isSaving,
    onSaveAndContinue,
    onDiscardAndContinue,
    onCancel,
  }
}
